/*
  The website's side of the same account the app uses.

  There is no separate user table anywhere: the Shopify customer record IS the
  account, found by phone number. So someone who has only ever ordered on the
  website logs into the app and finds their own orders, and the other way
  round. This file is only the website's half of that.

  Deliberately plain script — no modules, no build step, no framework. It has
  to run inside a Shopify theme that Rajesh can read and edit himself, and it
  must not add anything to the page weight that the shop does not need.
*/
(function () {
  'use strict';

  // The backend that holds the session and talks to Shopify with the Admin
  // token. If this host ever changes, it changes here and in the app's .env.
  var BACKEND = 'https://admin.shriposhak.com';

  // Where the signed session token lives. It carries no secret — it is a
  // customer id and an expiry, signed by the server so it cannot be altered
  // or invented. Thirty days, matching the app.
  var SESSION_KEY = 'shriposhak.session';

  function getSession() {
    try {
      return window.localStorage.getItem(SESSION_KEY) || null;
    } catch (e) {
      // Private browsing, or storage blocked. The shop still works; the
      // customer simply stays logged out.
      return null;
    }
  }

  function setSession(token) {
    try {
      window.localStorage.setItem(SESSION_KEY, token);
    } catch (e) {}
  }

  function clearSession() {
    try {
      window.localStorage.removeItem(SESSION_KEY);
    } catch (e) {}
  }

  // One way in and out of the backend, so every caller gets the same error
  // handling and nobody has to remember the headers.
  function post(path, body) {
    return fetch(BACKEND + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).then(function (response) {
      return response.json().catch(function () { return {}; }).then(function (json) {
        if (!response.ok) {
          // A 401 means the session is gone or expired. Drop it rather than
          // leaving a dead token to fail on every page from now on.
          if (response.status === 401) clearSession();
          throw new Error(json.error || 'Something went wrong. Please try again.');
        }
        return json;
      });
    });
  }

  var ACCOUNT = '/api/customer/store-credit';
  var WISHLIST = '/api/wishlist/sync';

  window.SP = {
    isLoggedIn: function () {
      return !!getSession();
    },

    logOut: function () {
      clearSession();
    },

    sendOtp: function (phone) {
      return post(ACCOUNT, { action: 'send_login_otp', phone: phone });
    },

    // Stores the session on success, so the caller only has to decide where
    // to send the customer next.
    verifyOtp: function (phone, otpCode, email) {
      return post(ACCOUNT, {
        action: 'verify_login_otp',
        phone: phone,
        otpCode: otpCode,
        email: email || undefined,
      }).then(function (json) {
        if (json.sessionToken) setSession(json.sessionToken);
        return json;
      });
    },

    profile: function () {
      return post(ACCOUNT, { action: 'profile', sessionToken: getSession() });
    },

    orders: function (count) {
      return post(ACCOUNT, { action: 'orders', sessionToken: getSession(), count: count });
    },

    // The delivery addresses on the account — the same ones the checkout
    // offers as chips, and the same ones the app lists. Saved against the
    // Shopify customer record, so an address added on the laptop is there
    // on the phone.
    //
    // The customer is taken from the signed session, never from anything
    // the browser sends, so nobody can add to or delete from someone
    // else's account by passing an id.
    createAddress: function (address) {
      return post(ACCOUNT, {
        action: 'create_address',
        sessionToken: getSession(),
        address: address,
      });
    },

    deleteAddress: function (addressId) {
      return post(ACCOUNT, {
        action: 'delete_address',
        sessionToken: getSession(),
        addressId: addressId,
      });
    },

    // Store Credit: the balance, and what moved it.
    //
    // This is the endpoint's original behaviour, which is why it asks for
    // nothing but a session and no `action` at all. It reads Shopify's own
    // store credit account for this customer — the very same one the app
    // reads, so there is no second wallet anywhere to drift out of step.
    // Cancel an order the customer has not been charged for and we have not
    // started shipping. The backend checks both again, and checks the order
    // is actually theirs — the session proves who is asking, and an order id
    // sent from a browser proves nothing at all.
    //
    // The field is called customerAccessToken for a historical reason: app
    // builds older than our own login still send a real Shopify token there.
    // The backend takes either.
    cancelOrder: function (orderId) {
      return post('/api/orders/cancel-order', {
        customerAccessToken: getSession(),
        orderId: orderId,
      });
    },

    // Where the parcel actually is, every scan of it, from the courier
    // through Shiprocket. Shopify only knows what each courier chooses to
    // tell it, and some tell it nothing at all.
    //
    // Rides the cancel-order route as an action for the same reason the app
    // does: Vercel's Hobby plan caps this backend at twelve functions and it
    // already has twelve.
    //
    // Resolves to null on any failure instead of rejecting. The order list
    // is useful without this, and an error message where a courier update
    // should be would be worse than nothing there at all.
    trackOrder: function (orderId) {
      return post('/api/orders/cancel-order', {
        customerAccessToken: getSession(),
        orderId: orderId,
        action: 'track',
      })
        .then(function (json) {
          return json && json.tracking ? json.tracking : null;
        })
        .catch(function () {
          return null;
        });
    },

    storeCredit: function () {
      return post(ACCOUNT, { sessionToken: getSession() }).then(function (json) {
        return {
          balanceRupees: Number(json.balanceRupees) || 0,
          history: json.history || [],
        };
      });
    },

    // The customer's own wishlist, held in Shopify against their account —
    // the same list the app reads and writes, so a heart tapped on a phone is
    // there on the laptop.
    // Asks for the products too, not just their ids. The stored wishlist is
    // only ids, and the storefront has no way to turn an id into a product —
    // so the backend, which already holds the Admin connection, does it.
    getWishlist: function () {
      return post(WISHLIST, {
        action: 'get',
        sessionToken: getSession(),
        withProducts: true,
      }).then(function (json) {
        return { productIds: json.productIds || [], products: json.products || [] };
      });
    },

    toggleWishlist: function (productId, wishlisted) {
      return post(WISHLIST, {
        action: 'toggle',
        sessionToken: getSession(),
        productId: productId,
        wishlisted: wishlisted,
      }).then(function (json) {
        return json.productIds || [];
      });
    },
  };
})();
